import {Tooltip} from 'react-tooltip'
import React, {useEffect, useState, useRef} from 'react'
import {toast} from 'react-toastify'
import {KTIcon} from '../../../_metronic/helpers'
import {Link, useNavigate} from 'react-router-dom'
import {ModeleEquipe} from '../../../components/Models'
import {deleteEquipe, getEquipes} from '../../../components/requests'
import {Pagination} from '../../../components/Pagination'
import {shouldDisplayFonctionnalite} from '../../../components/helpers'
import Swal from 'sweetalert2'
import {useAuth} from '../../modules/auth'
import {dayOrder, extractedHour, getDayLabel} from '../../../components/renders'

const EquipeListPage = () => {
  const [data, setData] = useState<Array<ModeleEquipe>>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [nombreDePages, setNombreDePages] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [spinner, setSpinner] = useState(true)
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchLoader, setSearchLoader] = useState<boolean>(false)
  const isFirstRender = useRef(true)

  const getAllEquipes = async () => {
    try {
      const response = await getEquipes(currentPage, searchTerm)

      if (response?.data !== undefined && response?.status === 200) {
        setData(response.data.data)
        setCurrentPage(response.data.page)
        setNombreDePages(response.data.pages)
        setTotalPages(response.data.total)
      }

      return response
    } catch (error) {
      console.log('error', error)
      throw error
    } finally {
      setSpinner(false)
    }
  }

  const removeEquipe = (equipeId: number | undefined) => {
    Swal.fire({
      title: 'Attention',
      text: 'Merci de confirmer la suppression',
      icon: 'warning',
      confirmButtonText: 'Confirmer',
      cancelButtonText: 'Annuler',
      showCancelButton: true,
      confirmButtonColor: '#1BC5BD',
      cancelButtonColor: '#F64E60',
    }).then((alertResult) => {
      if (alertResult.isConfirmed) {
        const idToast = toast.loading('Suppression en cours...')
        deleteEquipe(equipeId)
          .then((response) => {
            if (response?.status === 200) {
              const filteredData = data.filter((equipe) => equipe.id !== equipeId)
              setData(filteredData)
              toast.update(idToast, {
                render: 'Equipe supprimée avec succès.',
                type: 'success',
                isLoading: false,
                autoClose: 3000,
              })
            }
          })
          .catch((error) => {
            toast.update(idToast, {
              render: 'Erreur lors de la suppression.',
              type: 'error',
              isLoading: false,
              autoClose: 3000,
            })
          })
      }
    })
  }

  useEffect(() => {
    setSpinner(true)
    getAllEquipes()
  }, [currentPage])

  const handleSearch = async () =>  {
    setSearchLoader(true)

    try {
      await getAllEquipes()
    } catch (error) {
      console.error('Erreur', error)
    } finally {
      setSearchLoader(false)
    }
  }

  useEffect(function liveSearch() {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    const timer = setTimeout(() => {
      handleSearch()
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm])

  return (
    <>
      {/* begin::Row  */}
      <div className='row g-5 g-xl-8'>
        {/* begin::Col  */}
        <div className='col-lg-12 col-sm-12'>
          <div className={`card card-xxl-stretch mb-5 mb-xl-8`}>
            {shouldDisplayFonctionnalite(currentUser, 'VOIR EQUIPE') && (
              <>
                <div className='card-header border-0 pt-5'>
                  <div className='card-toolbar justify-content-between w-100'>
                    <div className={'position-relative ms-5'}>
                      {searchLoader ? (
                        <div
                          className={'position-absolute top-50 ms-5'}
                          style={{transform: 'translateY(-50%)'}}
                        >
                          <span className='spinner-border spinner-border-sm align-middle w-15px h-15px text-muted'></span>
                        </div>
                      ) : (
                        <i
                          className='ki-duotone ki-magnifier fs-2 text-lg-1 text-gray-500 position-absolute top-50 ms-5'
                          style={{transform: 'translateY(-50%)'}}
                        >
                          <span className='path1'></span>
                          <span className='path2'></span>
                        </i>
                      )}

                      <input
                        className='form-control form-control-solid px-15'
                        placeholder='Rechercher une équipe...'
                        type='text'
                        name='search'
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            // handleSearch()
                            handleSearch()
                          }
                        }}
                      />
                    </div>
                    <div>
                      {shouldDisplayFonctionnalite(currentUser, 'AJOUTER HORAIRE') && (
                        <Link
                          to={`/equipes/horaire/configurer`}
                          className='btn btn-sm btn-light-info me-3'
                        >
                          <KTIcon iconName='time' className='fs-3' />
                          Configurer une horaire
                        </Link>
                      )}
                      {shouldDisplayFonctionnalite(currentUser, 'AJOUTER EQUIPE') && (
                        <Link to={`/equipes/ajouter`} className='btn btn-sm btn-light-primary'>
                          <KTIcon iconName='plus' className='fs-3' />
                          Créer une équipe
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
                <div className='card-body py-3'>
                  {/* begin::Table container */}
                  <div className='table-responsive'>
                    {/* begin::Table */}
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                      {/* begin::Table head */}
                      <thead>
                        <tr className='fw-bold text-muted'>
                          <th>Libellé</th>
                          <th>Heure d'ouverture</th>
                          <th>Heure de pause</th>
                          <th>Heure de fermeture</th>
                          <th>Sauf le</th>
                          <th className='text-end'>Actions</th>
                        </tr>
                      </thead>
                      {/* end::Table head */}
                      {/* begin::Table body */}
                      <tbody>
                        {spinner ? (
                          <tr>
                            <td colSpan={6} className='p-4 text-center h-300px'>
                              <span className='spinner-border spinner-border-sm align-middle ms-2 w-30px h-30px'></span>
                            </td>
                          </tr>
                        ) : data.length === 0 ? (
                          <>
                            <tr>
                              <td colSpan={6} className='text-center'>
                                Aucun résultat trouvé
                              </td>
                            </tr>
                          </>
                        ) : (
                          data.map((equipe) => {
                            return (
                              <tr key={equipe?.id}>
                                <td>
                                  <p className='text-dark fw-bold text-hover-primary d-block fs-6 mb-0'>
                                    {equipe?.nom}
                                  </p>
                                </td>
                                <td>
                                  <p className='text-dark fw-bold text-hover-primary d-block fs-6 mb-0'>
                                    {extractedHour(equipe?.heureDebut)}
                                  </p>
                                </td>
                                <td>
                                  <p className='text-dark fw-bold text-hover-primary d-block fs-6 mb-0'>
                                    {extractedHour(equipe?.heureDebutPause)}
                                    {equipe?.heureDebutPause && equipe?.heureFinPause ? ' - ' : ' '}
                                    {extractedHour(equipe?.heureFinPause)}
                                  </p>
                                </td>
                                <td>
                                  <p className='text-dark fw-bold text-hover-primary d-block fs-6 mb-0'>
                                    {extractedHour(equipe?.heureFin)}
                                  </p>
                                </td>
                                <td>
                                  <div className='d-flex flex-column gap-1'>
                                    {Object.entries(equipe?.specificDay ?? {})
                                      .sort(
                                        ([dayA], [dayB]) =>
                                          dayOrder.indexOf(dayA) - dayOrder.indexOf(dayB)
                                      )
                                      .map(([day, hour]) => {
                                        const dayLabel = getDayLabel(day)

                                        if (!dayLabel) return null

                                        return (
                                          <div
                                            key={day}
                                            className='d-flex justify-content-between align-items-center'
                                            >
                                              <span className='text-dark fw-bold fs-7'>
                                                {dayLabel} :{' '}
                                                <span className='text-muted'>
                                                  ({hour?.heureDebut} - {hour?.heureFin})
                                                </span>
                                              </span>
                                          </div>
                                        )
                                      })}
                                  </div>
                                </td>
                                <td>
                                  <div className='d-flex justify-content-end flex-shrink-0'>
                                    {shouldDisplayFonctionnalite(
                                      currentUser,
                                      'MODIFIER HORAIRE'
                                    ) && (
                                      <p
                                        className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                        onClick={() =>
                                          navigate(`/equipes/horaire/${equipe?.id}/modifier`, {
                                            state: {
                                              equipe: {
                                                value: equipe?.id,
                                                label: equipe?.nom,
                                              },
                                            },
                                          })
                                        }
                                      >
                                        <KTIcon iconName='time' className='fs-3' />
                                      </p>
                                    )}
                                    {shouldDisplayFonctionnalite(
                                      currentUser,
                                      'MODIFIER EQUIPE'
                                    ) && (
                                      <p
                                        className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                        onClick={() =>
                                          navigate(`/equipes/${equipe?.id}/modifier`, {
                                            state: {data: equipe},
                                          })
                                        }
                                      >
                                        <i className='bi bi-pencil-square fs-2'></i>
                                      </p>
                                    )}
                                    {shouldDisplayFonctionnalite(currentUser, 'SUPPRIMER EQUIPE') &&
                                      !equipe?.isUsed && (
                                        <p
                                          className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                          onClick={() => removeEquipe(equipe?.id)}
                                        >
                                          <i className='bi bi-trash fs-2'></i>
                                        </p>
                                      )}
                                  </div>
                                </td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                      {/* end::Table body */}
                    </table>
                    {/* end::Table */}
                  </div>
                  {/* end::Table container */}

                  {/* begin::pagination*/}
                  {!spinner && (
                    <Pagination
                      totalPages={totalPages}
                      nombreDePages={nombreDePages}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      locationState={0}
                    />
                  )}
                  {/* end::pagination*/}
                  {/*Affichage du tooltip*/}
                  <Tooltip id='tooltip' />
                  {/*<ToastContainer position='bottom-right' autoClose={3000} />*/}
                </div>
              </>
            )}
          </div>
        </div>
        {/* end::Col  */}
      </div>
      {/* end::Row  */}
    </>
  )
}

export {EquipeListPage}
